import { Application, Container, Graphics } from "pixi.js";

import type { IStudioArea } from "../../../screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Areas.interface";
import {
  EStudioOverlayType,
  type IOverlayAspectRatio,
  type IStudioOverlay,
} from "../../../screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Overlay.interface";
import { migrateLegacyPanel } from "../../../screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Panel.interface";

import DrawCache from "./DrawCache";
import StrategyBounds from "./StrategyBounds";
import CustomOverlayRenderer from "./Overlays/CustomOverlayRenderer";

import {
  UpdaterList,
  type OverlayFrameUpdater,
} from "./Overlays/OverlayElementAnimator";

/**
 * Renders panels into the Pixi pipeline. Panels are now structurally
 * identical to custom overlays (a free-form `elements` list), so this
 * factory delegates to the same `CustomOverlayRenderer` used for overlays
 * — no per-type dispatch, no duplicated text/shape rendering paths.
 *
 * Legacy typed panels (lower_third / label / stats) are converted on the
 * fly via `migrateLegacyPanel` so old saved data keeps rendering.
 */
class PanelsFactory {
  private DrawCache: DrawCache;

  private Root: Container;

  private customRenderer: CustomOverlayRenderer;

  // Updaters per-frame (animaciones, partículas, procedural, typewriter) de
  // todas las áreas + reloj. Lista VIVA: las registraciones tardías caen acá y
  // `OnUpdater` le avisa al ticker de RenderFactory, que los corre por frame.

  public Anim: {
    updaters: OverlayFrameUpdater[];

    startMs: number;
  } | null = null;

  private OnUpdater: (() => void) | undefined;

  private Options = {
    width: 1920,

    height: 1080,
  };

  constructor(
    _app: Application,
    drawCache: DrawCache,
    root: Container,
    onUpdater?: () => void,
  ) {
    this.DrawCache = drawCache;

    this.Root = root;

    this.customRenderer = new CustomOverlayRenderer();

    this.OnUpdater = onUpdater;
  }

  public async renderPanels(areas: IStudioArea[]) {
    this.Anim = null;

    if (!areas || areas.length === 0) return;

    const updaters = new UpdaterList();

    updaters.onPush = this.OnUpdater;

    this.Anim = { updaters, startMs: Date.now() };

    for (const area of areas) {
      const rawPanel = area.panel;

      if (!rawPanel || !rawPanel.active) continue;

      const panel = migrateLegacyPanel(rawPanel);

      const panelKey = `panel_${area.id}`;

      const sizeArea = StrategyBounds.getBounds({
        bound: area.bounds,

        container: {
          width: this.Options.width,

          height: this.Options.height,
        },
      });

      let panelContainer = this.DrawCache.Containers[panelKey];

      if (!panelContainer || panelContainer.destroyed) {
        panelContainer = new Container();

        panelContainer.label = panelKey;

        this.DrawCache.Containers[panelKey] = panelContainer;

        this.Root.addChild(panelContainer);
      } else {
        for (const child of panelContainer.removeChildren()) {
          child.destroy({ children: true });
        }

        panelContainer.mask = null;
      }

      panelContainer.position.set(sizeArea.left, sizeArea.top);

      // Slightly above the overlay so panels visually crown the stack
      panelContainer.zIndex = (area.bounds.zIndex ?? 0) + 0.75;

      // Wrap as a synthetic custom overlay so the existing renderer pipeline
      // (which already handles elements, design-space scaling, hidden filter,
      // zIndex sort, glow, animations) can be reused unchanged.
      const syntheticOverlay: IStudioOverlay = {
        id: panel.id,
        label: panel.label,
        active: true,
        type: EStudioOverlayType.CUSTOM,
        options: {
          elements: panel.options.elements,
          designAspectRatio: panel.options.designAspectRatio as
            | IOverlayAspectRatio
            | undefined,
          variables: {},
        },
      };

      this.customRenderer.render(
        syntheticOverlay,
        panelContainer,
        { width: sizeArea.width, height: sizeArea.height },
        undefined,
        updaters,
      );

      // Clip panels so absolutely-positioned children stay inside the area rect
      const mask = new Graphics();

      mask.rect(0, 0, sizeArea.width, sizeArea.height).fill(0xffffff);

      panelContainer.addChild(mask);

      panelContainer.mask = mask;

      this.Root.sortDirty = true;
    }
  }

  public destroy() {
    this.Anim = null;

    for (const key in this.DrawCache.Containers) {
      if (key.startsWith("panel_")) {
        const container = this.DrawCache.Containers[key];

        if (container && !container.destroyed) {
          try {
            container.destroy({ children: true });
          } catch (e) {
            console.warn("Error destroying panel container:", e);
          }
        }

        delete this.DrawCache.Containers[key];
      }
    }
  }
}

export default PanelsFactory;
